// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-xl font-semibold gap-2 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]',
        accent:
          'bg-accent text-accent-foreground hover:bg-accent/90 active:scale-[0.98]',
        outline:
          'border border-border bg-transparent text-foreground hover:bg-secondary',
        'destructive-outline':
          'border border-destructive text-destructive hover:bg-destructive/5',
        ghost:
          'text-muted-foreground hover:bg-secondary',
      },
      size: {
        default: 'h-10 px-4 text-sm',
        sm:      'h-7 px-2.5 text-xs',
        full:    'h-12 px-6 w-full text-base',
        icon:    'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
